'use client'

import { useState } from 'react'
import Link from 'next/link'

import {
  Article,
  HeroSimple,
  Heading,
  Paragraph,
  Section,
  Form,
  FormGroup,
  FormInput,
  FormLabel,
  FormInvalidMessage,
  Button,
  ButtonTitle,
  ButtonIcon,
  cn,
} from '@systhemaui/next'

import { AuthCard, Separator } from '@/components/auth'
import { forgetPassword } from '@/lib/auth-client'

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('')
  const [error, setError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)

  const validateForm = (): boolean => {
    if (!email) {
      setError('Kötelező mező')
      return false
    }
    if (!/^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/i.test(email)) {
      setError('Érvénytelen e-mail cím')
      return false
    }
    setError('')
    return true
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) return

    setIsSubmitting(true)
    try {
      await forgetPassword({
        email,
        redirectTo: '/jelszo-visszaallitas',
      })
      // Always show success to prevent email enumeration
      setIsSuccess(true)
    } catch {
      // Still show success to prevent email enumeration
      setIsSuccess(true)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <main id="forgot-password">
      <Article theme="default">
        <HeroSimple alignContent="center">
          <Heading.h1>Elfelejtett jelszó</Heading.h1>
        </HeroSimple>

        <Section
          theme="default"
          layoutBackground="base"
          padding={3}
          className="pt-0 [&_.col-mx-3]:lg:col-span-12 [&_.col-mx-3]:lg:col-start-7"
        >
          <AuthCard>
            {isSuccess ? (
              <>
                <Heading.h6>E-mail elküldve</Heading.h6>
                <div className="flex flex-col gap-4 text-white">
                  <Paragraph>
                    A jelszó visszaállításához rövidesen küldeni fogunk egy e-mailt, amennyiben ez
                    kapcsolva van egy létező fiókhoz.
                  </Paragraph>
                  <Paragraph>
                    Az e-mail akár több percig is tarthat mire megérkezik.
                    <br />
                    Ne felejtsd el megnézni a spam mappát!
                  </Paragraph>
                </div>
              </>
            ) : (
              <>
                <Paragraph className="text-white">
                  Add meg az e-mail címet, amivel regisztráltál.
                  <br />A jelszó visszaállításához szükséges linket e-mail üzenetben küldjük el.
                </Paragraph>

                <Separator />

                <Form disableAnimation onSubmit={handleSubmit}>
                  <FormGroup>
                    <FormLabel htmlFor="email">E-mail cím</FormLabel>
                    <FormInput
                      type="email"
                      id="email"
                      name="email"
                      pattern="[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$"
                      placeholder="pl.: gipsz.jakab@creatortv.hu"
                      value={email}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setEmail(e.target.value)
                      }
                      className={cn(error && 'is-invalid')}
                      required
                    />
                    {error && <FormInvalidMessage>{error}</FormInvalidMessage>}
                  </FormGroup>

                  <div className="flex items-center gap-5 pt-6">
                    <Button type="submit" disabled={isSubmitting}>
                      <ButtonTitle>
                        {isSubmitting ? 'Betöltés...' : 'Jelszó visszaállítása'}
                      </ButtonTitle>
                      <ButtonIcon
                        dangerouslySetInnerHTML={{
                          __html:
                            '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"/></svg>',
                        }}
                      />
                    </Button>
                    <Link
                      href="/belepes"
                      className="text-[#FE4C04] underline transition-colors hover:text-[#FE4C04]/80"
                    >
                      Mégse
                    </Link>
                  </div>
                </Form>
              </>
            )}
          </AuthCard>
        </Section>
      </Article>
    </main>
  )
}
